import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter } from 'react-router-dom';
import './index.css';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import Layout from '@/components/Layout';
import ErrorBoundary from '@/components/ErrorBoundary';
import { App } from './app';

// Rotas carregadas de forma estática (entrada da app — devem ser imediatas)
import Login from '@/routes/Login';
import NotFound from '@/routes/NotFound';

// Rotas lazy — cada uma vira um chunk separado no build
const Cadastrar     = lazy(() => import('@/routes/Cadastrar'));
const Home          = lazy(() => import('@/routes/Home'));
const Separacao     = lazy(() => import('@/routes/Separacao'));
const Sobre         = lazy(() => import('@/routes/Sobre'));
const Duvidas       = lazy(() => import('@/routes/Duvidas'));
const Camera        = lazy(() => import('@/routes/Camera'));
const MapPage       = lazy(() => import('@/routes/Localizacao'));
const Perfil        = lazy(() => import('@/routes/Perfil'));
const PerfilPublico = lazy(() => import('@/routes/PerfilPublico'));
const EditarPerfil  = lazy(() => import('@/routes/EditarPerfil'));
const Ranking       = lazy(() => import('@/routes/Ranking'));
const Posts         = lazy(() => import('@/routes/Posts'));
const Feed          = lazy(() => import('@/routes/Feed'));
const Feedback      = lazy(() => import('@/routes/Feedback'));
const Desafios      = lazy(() => import('@/routes/Desafios'));

// Fallback compartilhado durante o carregamento de cada chunk
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#1A211A] to-[#2a3a2a]">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-4 border-white/20 border-t-[#EE9300] rounded-full animate-spin" />
      <span className="text-white/60 text-sm">Carregando...</span>
    </div>
  </div>
);

// Helper para envolver rotas protegidas com Suspense + ErrorBoundary
const Protected = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary>
    <ProtectedRoute>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </ProtectedRoute>
  </ErrorBoundary>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: <ErrorBoundary><Login /></ErrorBoundary>,
      },
      {
        path: 'Cadastrar',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Cadastrar />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      { path: 'Home',             element: <Protected><Home /></Protected> },
      { path: 'Separacao',        element: <Protected><Separacao /></Protected> },
      { path: 'Sobre',            element: <Protected><Sobre /></Protected> },
      { path: 'Duvidas',          element: <Protected><Duvidas /></Protected> },
      { path: 'Camera/:desafioId',element: <Protected><Camera /></Protected> },
      { path: 'Camera',           element: <Protected><Camera /></Protected> },
      { path: 'Localizacao',      element: <Protected><MapPage /></Protected> },
      { path: 'Perfil',           element: <Protected><Perfil /></Protected> },
      { path: 'perfil/:userId',   element: <Protected><PerfilPublico /></Protected> },
      { path: 'EditarPerfil',     element: <Protected><EditarPerfil /></Protected> },
      { path: 'Ranking',          element: <Protected><Ranking /></Protected> },
      { path: 'Posts',            element: <Protected><Posts /></Protected> },
      { path: 'feed',             element: <Protected><Feed /></Protected> },
      { path: 'Feedback',         element: <Protected><Feedback /></Protected> },
      { path: 'Desafios',         element: <Protected><Desafios /></Protected> },
      {
        path: '*',
        element: <ErrorBoundary><NotFound /></ErrorBoundary>,
      },
    ],
  },
]);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

ReactDOM.createRoot(rootElement).render(<App router={router} />);
